import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function formatPercentage(value: number | null | undefined, decimals: number = 1): string {
  if (value == null) return '--';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format agent directory name to display name.
 * e.g. "roamler_ooh_agent" -> "Roamler Ooh Agent"
 */
export function formatAgentName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get Tailwind text color class for a metric value (0-100 scale).
 */
export function getMetricColor(value: number | null | undefined): string {
  if (value == null) return 'text-muted-foreground';
  if (value >= 95) return 'text-green-400';
  if (value >= 80) return 'text-yellow-400';
  if (value >= 60) return 'text-orange-400';
  return 'text-red-400';
}

/**
 * Get Tailwind background + border classes for a metric value (0-100 scale).
 */
export function getMetricBg(value: number | null | undefined): string {
  if (value == null) return 'bg-muted/15 border-muted/30';
  if (value >= 95) return 'bg-green-500/15 border-green-500/30';
  if (value >= 80) return 'bg-yellow-500/15 border-yellow-500/30';
  if (value >= 60) return 'bg-orange-500/15 border-orange-500/30';
  return 'bg-red-500/15 border-red-500/30';
}

/**
 * Get Tailwind bg color class for chat evaluation scores (1-5 scale).
 */
export function getScoreColor(score: number): string {
  if (score >= 4.5) return 'bg-green-500';
  if (score >= 3.5) return 'bg-yellow-500';
  if (score >= 2.5) return 'bg-orange-500';
  return 'bg-red-500';
}

/**
 * Get human-readable category for chat evaluation scores (1-5 scale).
 */
export function getScoreCategory(score: number): string {
  if (score >= 4.5) return 'Perfect';
  if (score >= 3.5) return 'Good';
  if (score >= 2.5) return 'Fair';
  return 'Poor';
}
